import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { EdgeDefinition } from 'cytoscape';

import {
    AvancementCodeEnum,
    AvancementLabelEnum,
    Direction,
    type FiberByFunction,
    FiberDirection,
    type GraceThdSynopticNodeDataDefinition,
    NodeType,
    type SynopticNodeData,
} from '@connect-services/client/components/synoptic/synoptic-graph/graph-core/synoptic-graph.model';
import { SynopticGraphBuilderService } from '@connect-services/client/services/synoptic/graph-builder/synoptic-graph-builder.service';
import { GraceThdSynopticItem } from '@connect-services/src/sdk/api';

@Injectable({
    providedIn: 'root',
})
export class GraceThdSynopticGraphBuilderService extends SynopticGraphBuilderService<GraceThdSynopticItem> {
    private readonly translocoService = inject(TranslocoService);

    protected override buildEdge(item: GraceThdSynopticItem): EdgeDefinition {
        const data = {
            availableFibers: item.edge_disp,
            boxLabel: item.edge_etiquet,
            capacity: item.edge_capafo,
            dataId: item.edge, // real element id
            externalCode: item.edge_codeext,
            label: this.getEdgeLabel(item),
            source: item.from,
            status: item.edge_avancement,
            target: item.to,
            typeCode: 'edge',
            usableFibers: item.edge_util,
            uuid: item.edge_uuid,
        };

        const classes = ['edges', this.getElementColorCssClass(item.edge_avancement)];

        return { classes, data };
    }

    protected override buildNode(item: GraceThdSynopticItem, suffix: Direction): GraceThdSynopticNodeDataDefinition {
        const data: SynopticNodeData = {
            address: item[`${suffix}_adresse`],
            advancement: this.getAvancementName(item[`${suffix}_avancement`]),
            boxLabel: item[`${suffix}_etiquet`],
            codeExt: item[`${suffix}_pt_codeext`],
            externalCode: item[`${suffix}_codeext`],
            graceIdentifier: item[`${suffix}`],
            id: item[suffix],
            incomingFibersByFunction: this.formatFibersByState(
                item[`${suffix}_fibers_by_function`] as FiberByFunction[],
                FiberDirection.Entering,
            ),
            label: item[`${suffix}_etiquet`],
            logicalCodeType: item[`${suffix}_typelog_parent`],
            logicalType: item[`${suffix}_typelog_parent`],
            nbPto: this.getPtoCount(item[`${suffix}_nb_pto`]),
            numberVirtualRealCassettes: this.formatCassettesValues(
                item[`${suffix}_virtual_cassettes`],
                item[`${suffix}_real_cassettes`],
            ),
            outgoingFibersByFunction: this.formatFibersByState(
                item[`${suffix}_fibers_by_function`] as FiberByFunction[],
                FiberDirection.Outgoing,
            ),
            technicalPointType: item[`${suffix}_typelog_parent`],
            tiroir: item[`${suffix}_nb_tiroir`],
            title: '',
            type: item[`${suffix}_type`],
            typeCode: item[`${suffix}_type_code`],
            typephyParent: item[`${suffix}_typephy_parent`],
            uuid: item[`${suffix}_uuid`],
        };

        const classes = this.getNodeClasses(data, item[`${suffix}_avancement`]);

        this.finalizeNodeData(data, item, suffix, classes);

        return { classes, data };
    }

    protected override getRenderNodeLabels(
        element: cytoscape.CoreData,
        corpus: string,
        x: number,
        y: number,
    ): { corpus: string; y: number } {
        const labels: { id: string; label: string }[] = this.getRenderLabels();
        labels.forEach((property) => {
            const value = element.data(property.id);

            if (!value?.length || value === 'null') {
                return;
            }

            const text = `${property.label} : ${value}`;

            corpus += `<text x="${x}" y="${y}" font-family="Verdana" font-size="12" fill="blue" text-anchor="middle">${text}</text>`;
            y += 20;
        });

        return { corpus, y };
    }

    private finalizeNodeData(
        data: SynopticNodeData,
        item: GraceThdSynopticItem,
        suffix: Direction,
        classes: string[],
    ): void {
        if (data.type === NodeType.BAIE || data.type === NodeType.FERME) {
            const logicalTypeClass = data.logicalType === 'NŒUD RACCORDEMENT OPTIQUE' ? NodeType.NRO : NodeType.SRO;
            classes.push(logicalTypeClass);

            data.title = `${data.type} - ${data.externalCode}\n${data.logicalType} `;
            delete data.logicalType;

            return;
        }

        data.title = `${data.type} - ${data.logicalType}\n ${item[`${suffix}_pt_codeext`] ?? ''}`;
        delete data.physicalType;
        delete data.logicalType;
        delete data.tiroir;
    }

    private formatCassettesValues(virtualValue?: number | string | null, realValue?: number | string | null): string {
        const isDefined = (val?: number | string | null): boolean => {
            return !!val && String(val).trim() !== '';
        };

        const hasVirtual = isDefined(virtualValue);
        const hasReal = isDefined(realValue);

        if (!hasVirtual && !hasReal) {
            return this.translocoService.translate('notAvailable');
        }

        const left = hasVirtual ? String(virtualValue) : this.translocoService.translate('notAvailable');
        const right = hasReal ? String(realValue) : this.translocoService.translate('notAvailable');

        return `${left} / ${right}`;
    }

    private formatFibersByState(fibersByFunction: FiberByFunction[] | undefined, state: FiberDirection): string {
        if (!Array.isArray(fibersByFunction)) {
            return '';
        }

        return fibersByFunction
            .map((fiber: FiberByFunction): string => `${fiber.function} : ${fiber[state]}`)
            .join(', ');
    }

    private getAvancementName(avancement: string): string {
        switch (avancement) {
            case AvancementCodeEnum.ToCreate:
                return this.translocoService.translate(AvancementLabelEnum.ToCreate);
            case AvancementCodeEnum.Maintenance:
                return this.translocoService.translate(AvancementLabelEnum.Maintenance);
            case AvancementCodeEnum.Working:
                return this.translocoService.translate(AvancementLabelEnum.Working);
            case AvancementCodeEnum.NotWorking:
                return this.translocoService.translate(AvancementLabelEnum.NotWorking);
            case AvancementCodeEnum.Discarded:
                return this.translocoService.translate(AvancementLabelEnum.Discarded);
            case AvancementCodeEnum.Existing:
                return this.translocoService.translate(AvancementLabelEnum.Existing);
            default:
                return this.translocoService.translate(AvancementLabelEnum.Unknown);
        }
    }

    private getEdgeLabel(row: GraceThdSynopticItem): string {
        const length = Number(row.edge_long).toLocaleString('fr-FR', {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
        });

        const label = `${this.translocoService.translate('synoptic.label')} : ${row.edge_etiquet}`;
        const cableInformation = `${this.translocoService.translate('synoptic.cable')} ${row.edge_capafo} FO (${row.edge_util} FO Utiles, ${row.edge_disp} FO ${this.translocoService.translate('available')}) - ${length} m`;
        const edgeTypelog = row.edge_typelog;
        const graceId = `${this.translocoService.translate('synoptic.graceId')} : ${row.edge}`;
        const externalCode = `${this.translocoService.translate('synopticPlan.externalCode')} : ${row.edge_codeext}`;
        const theoreticalCoiling = `${this.translocoService.translate('synoptic.theoreticalCoiling')} ${row.edge_love ? `${row.edge_love} m` : this.translocoService.translate('notAvailable')}`;

        const lines = [label, cableInformation, edgeTypelog, '', graceId, externalCode, theoreticalCoiling];

        return lines.join('\n');
    }

    private getNodeClasses(data: SynopticNodeData, advancement: string): string[] {
        const classes = ['nodes', this.getElementColorCssClass(advancement ?? '')];

        if (data.nbPto) {
            classes.push('has-pto');
        }

        if (data.typeCode === NodeType.BAIE || data.typeCode === NodeType.FERME) {
            classes.push('is-bay');
        }

        if (data.typeCode === NodeType.BPE || data.typeCode === NodeType.PBO) {
            classes.push('is-box');
        }

        return classes;
    }

    private getPtoCount(rawValue: string): number | undefined {
        const value = parseInt(rawValue, 10);
        if (Number.isNaN(value)) {
            return undefined;
        }

        return value;
    }

    private getRenderLabels(): { id: string; label: string }[] {
        return [
            { id: 'technicalPointType', label: this.translocoService.translate('synoptic.technicalPointType') },
            { id: 'boxLabel', label: this.translocoService.translate('synoptic.boxLabel') },
            { id: 'graceIdentifier', label: this.translocoService.translate('synoptic.graceIdentifier') },
            { id: 'externalBox', label: this.translocoService.translate('synoptic.externalBox') },
            { id: 'address', label: this.translocoService.translate('synoptic.address') },
            { id: 'advancement', label: this.translocoService.translate('advancement') },
            {
                id: 'numberVirtualRealCassettes',
                label: this.translocoService.translate('synoptic.numberVirtualRealCassettes'),
            },
            {
                id: 'incomingFibersByFunction',
                label: this.translocoService.translate('synoptic.numberIncomingFibers'),
            },
            {
                id: 'outgoingFibersByFunction',
                label: this.translocoService.translate('synoptic.numberOutgoingFibers'),
            },
            { id: 'nbPto', label: this.translocoService.translate('synoptic.nbPto') },
        ];
    }
}
